import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Search, ListTodo } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { TaskBoard } from "../components/tasks/TaskBoard";
import { NewTaskDialog } from "../components/tasks/NewTaskDialog";
import { TaskDetailPanel } from "../components/tasks/TaskDetailPanel";
import { TYPE_META } from "../components/tasks/taskConstants";

export default function TasksBoardPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [detailTaskId, setDetailTaskId] = useState(searchParams.get("task") || null);

  // Fix 2026-10-28 (testing iter120): cada pulsación disparaba una llamada a
  // la API -- se espera 350ms de pausa antes de buscar.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "all") params.type = typeFilter;
      if (debouncedQ) params.q = debouncedQ;
      const r = await api.listTasks(token, params);
      setTasks(r.items || []);
    } catch (e) {
      toast.error("No se pudieron cargar las tareas");
    } finally {
      setLoading(false);
    }
  }, [token, typeFilter, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  const openTask = (id) => {
    setDetailTaskId(id);
    setSearchParams({ task: id });
  };
  const closeDetail = (open) => {
    if (!open) { setDetailTaskId(null); searchParams.delete("task"); setSearchParams(searchParams); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="tasks-board-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ListTodo className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">Tablero de tareas</h1>
        </div>
        <Button onClick={() => setNewOpen(true)} className="gap-1.5" data-testid="tasks-new-button">
          <Plus className="w-4 h-4" />Nueva tarea
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título..." className="pl-8" data-testid="tasks-search-input" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="tasks-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando tablero...</p>
      ) : (
        <TaskBoard tasks={tasks} onOpenTask={openTask} onChanged={load} />
      )}

      <NewTaskDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
      <TaskDetailPanel taskId={detailTaskId} open={!!detailTaskId} onOpenChange={closeDetail} onChanged={load} />
    </div>
  );
}
